import React, { useEffect, useState } from 'react';
import ReactDOM from "react-dom";

import axiosInstance from './axios';
import { isValidMFACode } from './validation';
import Toast from '../components/toast';
import QRCode from "react-qr-code";

import {
	Backdrop,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Link,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { set } from 'date-fns';


export default function MFASetupDialog({ dialogOpen, setDialogOpen }) {

	const [loading, setLoading] = useState(false);
	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});
	const [otpAuthURI, setOTPAuthURI] = useState('');
	const [mfaCode, setMFACode] = useState('');
	const [backupCodes, setBackupCodes] = useState([]);
	const [setupStep, setSetupStep] = useState(true);
	const [qrDisplay, setQRDisplay] = useState(true);

	// Password conditions
	const [mfaCodeValid, setMFACodeValid] = useState(false);

	const checkMFACodeValid = () => { setMFACodeValid(isValidMFACode(mfaCode)) };

	// Check if new password meets all requirements
	useEffect(() => {
		if (mfaCode.length > 0) {
			checkMFACodeValid();
		}
	}, [mfaCode]);

	// Call MFA activation API endpoint on dialog load
	useEffect(() => {
		if (dialogOpen) {
			mfaActivate();
		}
	}, [dialogOpen]);

	// Clear the contents of the dialog
	const mfaActivate = () => {
		axiosInstance.post(`auth/app/activate/`, {})
			.then((res) => {
				if (res.data && res.data.details) {
					setOTPAuthURI(res.data.details);
				} else {
					console.log('Invalid response from server:', res);
				}
			})
			.catch((error) => {
				setToast({ severity: 'error', message: 'Error trying to activate MFA.', open: true });
				console.error('Error activating App MFA:', error);
			});
	};

	// Call self password reset API endpoint
	const mfaActivateConfirm = () => {
		setLoading(true);
		axiosInstance.post(`auth/app/activate/confirm/`, {
				code: mfaCode,
			})
			.then((res) => {
				if (res.data && res.data.backup_codes) {
					setBackupCodes(res.data.backup_codes);
					setSetupStep(false);
				}
			})
			.catch((error) => {
				setToast({ severity: 'error', message: `MFA activation failed. ${error}`, open: true });
				console.log(`MFA activation failed: ${error}`);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	// Get MFA secret from URI
	function getSecretFromURI(uri) {
		const url = new URL(uri);
		
		const params = new URLSearchParams(url.search);
		
		return params.get('secret');
	}

	// Handle dialog clearing
	const clearDialog = () => {
		setOTPAuthURI('');
		setMFACode('');
		setBackupCodes([]);
	};

	// Handle dialog closing
	const handleDialogClose = (event, reason) => {
		// Prevent closing on backdrop click during the setup step
		if (setupStep && reason && reason === "backdropClick") {
			return;
		}

		setDialogOpen(false);
		// clearDialog();
	};

	// Handle button clicks
	const handleQRSwitchClick = () => {
		setQRDisplay(!qrDisplay);
	};

	const handleVerifyClick = () => {
		if (mfaCodeValid) {
			mfaActivateConfirm();
		} else {
			setToast({ severity: 'error', message: 'MFA code not valid', open: true });
		}
	};

	const handleCancelClick = () => {
		handleDialogClose();
	};

	const handleCloseClick = () => {
		handleDialogClose();
	}

	// Handle textfield password updates
	const handleMFACodeChange = (event) => {
		setMFACode(event.target.value);
	};

	// Dialog pages
	const setupPageContent = (
		<DialogContent>
			<Typography variant="h6" component="div" fontFamily='Saira, sans-serif' fontWeight={600} sx={{ flexGrow: 1 }}>
				Step 1: { qrDisplay ? 'Scan QR Code' : 'Copy and Paste Key'}
			</Typography>
			<Typography component="div" fontFamily='Saira, sans-serif' sx={{ marginLeft: 2, flexGrow: 1 }}>
				{ qrDisplay ?
					'Scan the QR code below with your authenticator app (e.g. Google Authenticator, Microsoft Authenticator, etc.)' :
					'Paste the key below into your authenticator app (e.g. Google Authenticator, Microsoft Authenticator, etc.)'
				}
			</Typography>
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: 1 }}>
				{ qrDisplay ? (
					<QRCode value={otpAuthURI} size={300} />
				) : (
					<Box sx={{ textAlign: 'center', bgcolor: 'lightgray', paddingX: 1, marginY: 1 }}>
						<Typography variant='h6' component='div' fontFamily='Saira, sans-serif' fontWeight={600}>
							{ otpAuthURI ? getSecretFromURI(otpAuthURI) : "Error loading MFA secret key. Please refresh the page and try again." }
						</Typography>
					</Box>
				) }
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				<Link onClick={handleQRSwitchClick} variant='body2' style={{ textAlign: 'center', cursor: 'pointer' }}>
					{ qrDisplay ? "Can't scan QR code?" : "Want to scan QR code?" }
				</Link>
			</Box>
			<Divider sx={{ marginY: 2 }} />
			<Typography variant="h6" component="div" fontFamily='Saira, sans-serif' fontWeight={600} sx={{ flexGrow: 1 }}>
				Step 2: Enter MFA code
			</Typography>
			<Typography component="div" fontFamily='Saira, sans-serif' sx={{ marginLeft: 2, flexGrow: 1 }}>
				Enter the 6-digit MFA code generated by the authenticator app
			</Typography>
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
			<TextField
				id='mfaCode'
				name='mfaCode'
				label='MFA Code'
				required
				value={mfaCode}
				onChange={(event) => setMFACode(event.target.value)}
				inputProps={{ maxLength: 6 }}
				error={mfaCode.length > 0 && !mfaCodeValid}
				helperText={(mfaCode && !mfaCodeValid) ? 'Invalid MFA code' : ''}
				variant='outlined'
				margin='normal'
				sx={{textAlign: 'center'}}
				// fullWidth
				autoFocus
			/>
			</Box>
		</DialogContent>
	);

	const BackupCodesTable = ({ codes }) => {
		// Split the codes array into rows of 2 codes each
		const rows = [];
		for (let i = 0; i < codes.length; i += 2) {
			rows.push(codes.slice(i, i + 2));
		}
	
		return (
			<TableContainer component={Paper} sx={{ marginX: 8, marginTop: 2, backgroundColor: 'lightgray' }}>
				<Table>
					<TableBody>
						{rows.map((row, index) => (
							<TableRow key={index}>
								<TableCell sx={{ textAlign: 'center' }}>
									<Typography fontWeight={600}>{row[0]}</Typography>
								</TableCell>
								<TableCell sx={{ textAlign: 'center' }}>
									<Typography fontWeight={600}>{row[1]}</Typography>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	};

	// Saves the backup codes to a txt file and downloads it
	const downloadBackupCodes = (codes) => {
		const element = document.createElement("a");
		const fileContents = '-- OBVM2 Backup Codes --\n\n' + codes.join('\n') + '\n\n-- Generated: ' + new Date().toLocaleString() + ' --';
		const file = new Blob([fileContents], { type: 'text/plain' });
		element.href = URL.createObjectURL(file);
		element.download = "obvm2_backup_codes.txt";
		document.body.appendChild(element); // Required for this to work in FireFox
		element.click();
	};

	const backupCodePageContent = (
		<DialogContent>
			<Typography component="div" fontFamily='Saira, sans-serif' sx={{ marginLeft: 2, flexGrow: 1 }}>
				If you ever lose access to your device, you will need these backup codes to log in. Each code can only be used a single time. We highly recommend that you save them in a safe place.
			</Typography>
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: 1 }}>
				<BackupCodesTable codes={backupCodes} />
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: 2 }}>
				<Button variant="outlined" size='small' onClick={() => downloadBackupCodes(backupCodes)}>
					Download Backup Codes
				</Button>
			</Box>
			<Divider />
		</DialogContent>
	);

	return (
		<>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
			/>
			<Dialog
				open={dialogOpen}
				onClose={handleDialogClose}
				aria-labelledby="form-dialog-title"
				maxWidth='sm'
				sx={{
					'.MuiDialog-paper': {
						border: '5px solid #B1282E',
						borderRadius: 3
					},
				}}
			>
				<Backdrop
					sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
					open={loading}
				>
					<CircularProgress color="inherit" />
				</Backdrop>
				<DialogTitle id="form-dialog-title">
					<Typography variant="h4" component="div" fontFamily='Saira, sans-serif' fontWeight={600} textAlign='center' sx={{ flexGrow: 1 }}>
						{ setupStep ? 'Setup MFA' : 'MFA Backup Codes' }
					</Typography>
				</DialogTitle>
				{ setupStep ? setupPageContent : backupCodePageContent }
				<DialogActions sx={{ justifyContent: 'center', paddingBottom: 3 }}>
					{ setupStep ? (
						<>
							<Button onClick={handleVerifyClick} variant='contained' size='large' disabled={!mfaCodeValid}>
								Verify
							</Button>
							<Button onClick={handleCancelClick} variant='outlined' size='large'>
								Cancel
							</Button>
						</>
					) : (
						<Button onClick={handleCloseClick} variant='contained' size='large'>
							I've saved my backup codes
						</Button>
					) }
				</DialogActions>
			</Dialog>
		</>
	);
}